import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { zodResolver } from "mantine-form-zod-resolver";
import { httpPostGraphql } from "shared/api/httpClient";
import {
    createIpawsAlertFormState,
    IpawsAlertFormProvider,
    ipawsAlertFormSteppedStateSchema,
    useIpawsAlertForm,
} from "shared/components/ipaws/alert/context";
import { IpawsTemplateFormLayout } from "shared/components/ipaws/template/IpawsTemplateFormLayout";
import { useCustomer } from "shared/graphql/cust";
import { usePermissionCheck } from "shared/stores/oidc";
import { to } from "shared/utils/fns";
import { NoMessagesFigure } from "src/assets/NoMessagesFigure";
import { graphql } from "src/gql";
import { Permission } from "src/gql/graphql";
import { queryClient } from "src/queryClient";

const Page = () => {
    const hasIpawsView = usePermissionCheck(Permission.IpawsAlertModify);
    if (!hasIpawsView) return <NoMessagesFigure>You do not have permission to view this page</NoMessagesFigure>;

    const { id } = Route.useParams();
    const { data: customer } = useCustomer();
    const {
        data: { alert },
    } = useSuspenseQuery(query(id));

    const form = useIpawsAlertForm({
        cascadeUpdates: true,
        initialValues: createIpawsAlertFormState(alert.capMessage, {
            mode: ["edit", id],
            customerId: customer.id,
            certId: alert.certificate.id,
            durationMinutes: alert.durationMinutes,
        }),
        validate: zodResolver(ipawsAlertFormSteppedStateSchema),
    });

    return (
        <IpawsAlertFormProvider form={form}>
            <IpawsTemplateFormLayout draftId={alert.id} />
        </IpawsAlertFormProvider>
    );
};

const query = (id: string) =>
    queryOptions({
        queryKey: ["ipaws", "templates", id],
        select: to("ipawsTemplate"),
        queryFn: () =>
            httpPostGraphql(
                graphql(`
                    query IpawsTemplate($id: UUID!) {
                        ipawsTemplate(id: $id) {
                            alert {
                                id
                                certificate {
                                    id
                                }
                                capMessage {
                                    ...IpawsCapMessageDetails
                                }
                                durationMinutes
                            }
                        }
                    }
                `),
                { id },
            ),
    });

/** @public */
export const Route = createFileRoute("/_auth/ipaws/templates/$id")({
    component: Page,
    async beforeLoad({ params: { id } }) {
        await queryClient.ensureQueryData(query(id));
    },
});
